'use client'

import { memo } from 'react'
import { Heart, GitFork, MessageSquare, Lock, FileText, MoreHorizontal, Pin, Pencil, Trash2, FolderPlus } from 'lucide-react'
import { useCollectionsStore } from '../_stores/collections-store'
import { usePinPrompt, useUnpinPrompt } from '../_hooks/use-pinned-prompts'
import type { PromptGridItem } from '@/types/collection.types'
import { sanitizeHtml } from '@/lib/sanitize-html'
function getRelativeTime(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime()
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

interface PromptCardGridProps {
  prompt: PromptGridItem
  isOwnView: boolean
  userId: string
  onAddToCollection?: (promptId: string) => void
  onDelete?: (promptId: string) => void
  /** ID of the prompt whose menu is currently open (lifted state) */
  openMenuId?: string | null
  /** Callback to toggle a prompt's menu (lifted state) */
  onMenuToggle?: (promptId: string | null) => void
}

/**
 * Individual prompt card for the grid (own view).
 * Shows: checkbox, category bar, title, description, tags, stats, visibility badge, collections footer.
 */
export const PromptCardGrid = memo(function PromptCardGrid({ prompt, isOwnView, userId, onAddToCollection, onDelete, openMenuId, onMenuToggle }: PromptCardGridProps) {
  const isSelected = useCollectionsStore((s) => s.selectedPromptIds.has(prompt.id))
  const togglePromptSelection = useCollectionsStore((s) => s.togglePromptSelection)

  const menuOpen = openMenuId === prompt.id
  const setMenuOpen = (open: boolean) => onMenuToggle?.(open ? prompt.id : null)

  const pinPrompt = usePinPrompt(userId)
  const unpinPrompt = useUnpinPrompt(userId)

  const visibleTags = prompt.tags.slice(0, 3)
  const extraTagCount = prompt.tags.length - 3

  const timeAgo = getRelativeTime(prompt.createdAt)

  const handlePin = () => {
    const scrollY = window.scrollY
    const restore = () => requestAnimationFrame(() => window.scrollTo(0, scrollY))
    if (prompt.isPinned) {
      unpinPrompt.mutate({ promptId: prompt.id }, { onSettled: restore })
    } else {
      pinPrompt.mutate({ promptId: prompt.id }, { onSettled: restore })
    }
  }

  return (
    <div
      className={`
        group relative rounded-[var(--radius-md)] border transition-all duration-200
        ${isSelected ? 'border-[var(--color-brand-indigo)] bg-[var(--color-brand-indigo)]/5' : 'border-[var(--color-border)] bg-[var(--color-bg-surface)]'}
        ${menuOpen ? 'z-[100]' : ''}
        hover:-translate-y-0.5 hover:shadow-md
      `}
    >
      {/* Category color bar */}
      <div className="absolute left-0 top-0 h-full w-1 rounded-l-[var(--radius-md)] bg-[var(--color-brand-indigo)]" />

      <div className="p-4 pl-5">
        {/* Top row: checkbox + title + context menu */}
        <div className="mb-2 flex items-start gap-2">
          {isOwnView && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => togglePromptSelection(prompt.id)}
              aria-label={`Select ${prompt.title}`}
              className="mt-1 h-4 w-4 shrink-0 rounded border-[var(--color-border)] text-[var(--color-brand-indigo)]"
            />
          )}

          <div className="min-w-0 flex-1">
            <a
              href={`/p/${prompt.usernameRef}/${prompt.slug}`}
              className="line-clamp-1 text-sm font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-brand-indigo)]"
            >
              {prompt.isPinned && <Pin className="mr-1 inline h-3.5 w-3.5 text-[var(--color-brand-amber)]" />}
              {prompt.title}
            </a>
          </div>

          {isOwnView && (
            <div className="relative shrink-0">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="-mr-1.5 rounded-[var(--radius-md)] p-2 text-[var(--color-text-muted)] opacity-0 transition-all hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)] group-hover:opacity-100"
                aria-label="More options"
                aria-expanded={menuOpen}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-[998]" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full z-[999] mt-1 w-48 rounded-[var(--radius-md)] border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-[#111118]">
                    {/* Pin / Unpin */}
                    <button
                      onClick={() => { handlePin(); setMenuOpen(false) }}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      <Pin className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      {prompt.isPinned ? 'Unpin' : 'Pin to top'}
                    </button>

                    {/* Add to Collection */}
                    {onAddToCollection && (
                      <button
                        onClick={() => { onAddToCollection(prompt.id); setMenuOpen(false) }}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                      >
                        <FolderPlus className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        Add to Collection
                      </button>
                    )}

                    {/* Edit */}
                    <a
                      href={`/p/${prompt.usernameRef}/${prompt.slug}/edit`}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      <Pencil className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      Edit
                    </a>

                    <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

                    {/* Delete */}
                    <button
                      onClick={() => { onDelete?.(prompt.id); setMenuOpen(false) }}
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:text-red-400 dark:hover:bg-white/10"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Description - render TipTap HTML with formatting */}
        {(prompt.readmeContent || prompt.promptContent) && (
          <div
            className="tiptap-content mb-3 line-clamp-2 !min-h-0 text-xs text-[var(--color-text-secondary)] [&_p]:mb-0"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(prompt.readmeContent || prompt.promptContent || '') }}
          />
        )}

        {/* Tags */}
        {prompt.tags.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] text-[var(--color-text-secondary)]"
              >
                {tag}
              </span>
            ))}
            {extraTagCount > 0 && (
              <span className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)]">
                +{extraTagCount} more
              </span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="mb-2 flex items-center gap-3 text-[10px] text-[var(--color-text-muted)]">
          <span className="flex items-center gap-1">
            <Heart className="h-3 w-3" /> {prompt.starCount}
          </span>
          <span className="flex items-center gap-1">
            <GitFork className="h-3 w-3" /> {prompt.forkCount}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {prompt.commentCount}
          </span>
        </div>

        {/* Visibility + timestamp */}
        <div className="flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]">
          {prompt.visibility === 'private' && (
            <span className="flex items-center gap-1">
              <Lock className="h-3 w-3" /> Private
            </span>
          )}
          {prompt.visibility === 'unlisted' && (
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" /> Unlisted
            </span>
          )}
          <span>Created {timeAgo}</span>
        </div>

        {/* Collections footer (own view) */}
        {isOwnView && prompt.collectionIds.length > 0 && (
          <div className="mt-2 border-t border-[var(--color-border)] pt-2 text-[10px] text-[var(--color-text-muted)]">
            Saved to {prompt.collectionIds.length} collection{prompt.collectionIds.length > 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  )
})
